import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, XCircle, RefreshCw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUpdateSubscriptionChannel, Subscription, getUserChannel } from '../../services/subscriptionService'; // Adjusted path
import toast from 'react-hot-toast';

interface ChangeChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeSubscription: Subscription; // Full active subscription object
  currentPlanName: string; // To display in the title
  onChannelUpdateSuccess?: () => void;
  onChannelUpdateError?: (error: Error) => void;
}

export default function ChangeChannelModal({
  isOpen,
  onClose,
  activeSubscription,
  currentPlanName,
  onChannelUpdateSuccess,
  onChannelUpdateError,
}: ChangeChannelModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [channelToUpdateTo, setChannelToUpdateTo] = useState<string | null>(null);

  const { data: channels, isLoading: isLoadingChannels, error: channelsError } = useQuery({
    queryKey: ['userChannels'],
    queryFn: getUserChannel,
  });
  const { mutate: updateChannel, isPending: isUpdatingChannel, error: updateChannelErrorApi } = useUpdateSubscriptionChannel(queryClient);

  // Reset selected channel when modal opens or active subscription changes
  useEffect(() => {
    if (isOpen) {
      setChannelToUpdateTo(null);
    }
  }, [isOpen]);

  const handleConfirmChange = () => {
    if (!activeSubscription || !channelToUpdateTo || isUpdatingChannel) {
      return;
    }
    
    updateChannel({ subscriptionId: activeSubscription.id, newChannelId: channelToUpdateTo }, {
      onSuccess: () => {
        toast.success(t('channelUpdatedSuccessfully')); // Replace with better UI notification
        if (onChannelUpdateSuccess) onChannelUpdateSuccess();
        onClose(); // Close modal on success
      },
      onError: (error: Error) => {
         toast.error(t('errorMedia', { error: error.message || t('failedToUpdateChannel') })); // Replace with better UI
         if (onChannelUpdateError) onChannelUpdateError(error);
         // Optionally keep modal open on error, or let parent decide
      }
    });
  };

  const handleCloseModal = () => {
    setChannelToUpdateTo(null); // Reset selection
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {activeSubscription.channel 
              ? t('changeChannelFor', { planName: currentPlanName })
              : t('assignChannelFor', { planName: currentPlanName })
            }
          </h2>
          <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {updateChannelErrorApi && <p className="text-red-500 text-sm mb-2">{t('errorUpdatingChannel', { error: (updateChannelErrorApi as Error).message })}</p>}

        <div className="mb-4">
            <label htmlFor="channelSelect" className="block text-sm font-medium text-gray-700 mb-1">
                {t('availableChannels')}
            </label>
            {isLoadingChannels && <div className="flex items-center"><Loader2 className="animate-spin w-5 h-5 mr-2 text-gray-500" /> {t('loadingChannels')}</div>}
            {channelsError && <p className="text-red-500 text-sm">{t('errorLoadingChannels', { error: (channelsError as Error).message })}</p>}
            {channels && !isLoadingChannels && (
            <select
                id="channelSelect"
                value={channelToUpdateTo || ''}
                onChange={(e) => setChannelToUpdateTo(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={isLoadingChannels || isUpdatingChannel}
            >
                <option value="" disabled>{t('selectNewChannel')}</option>
                {channels
                .filter(c => !activeSubscription.channel || c.id !== activeSubscription.channel.id) // Exclude current channel if exists
                .map(channel => (
                    <option key={channel.id} value={channel.id}>{channel.nameChannel}</option> // Added fallback for channel name
                ))}
            </select>
            )}
            {channels && channels.filter(c => !activeSubscription.channel || c.id !== activeSubscription.channel.id).length === 0 && !isLoadingChannels && (
                <p className="text-sm text-gray-500 mt-2">{t('noOtherChannelsAvailable')}</p>
            )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCloseModal}
            className="py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isUpdatingChannel}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleConfirmChange}
            disabled={!channelToUpdateTo || isUpdatingChannel || isLoadingChannels || channels?.filter(c => !activeSubscription.channel || c.id !== activeSubscription.channel.id).length === 0}
            className="py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isUpdatingChannel && <RefreshCw className="animate-spin w-4 h-4" />}
            <span>{activeSubscription.channel ? t('changeChannelConfirm') : t('assignChannelConfirm')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
